// ./src/hooks/useAutoLocation.ts

// ============================================
// useAutoLocation Hook
// Convenience hook for auto-requesting location when drawers/modals open.
//
// Thin wrapper around LocationHistoryContext.requestLocation.
// GPS coordinates are always fetched fresh — no caching at this level.
// Geocode result caching is handled inside LocationHistoryContext.
// ============================================

import { useState, useEffect, useCallback, useRef } from "react";

import { GeoCoords } from "@/hooks/useGeolocation";
import { AddressData } from "@/hooks/useGoogleMapsAutocomplete";

import { useLocationHistoryContext } from "@/contexts/LocationHistoryContext";
import {
  CachedLocation,
  UseAutoLocationOptions,
  UseAutoLocationReturn,
} from "@/contexts/LocationHistoryContext/types";

// ============================================
// Hook
// ============================================

/**
 * Hook for automatically requesting location when a component becomes active
 * (e.g., when a drawer opens).
 *
 * GPS coordinates are always fetched fresh on each enable cycle.
 * Reverse geocode API results are cached inside LocationHistoryContext
 * to avoid redundant API calls when the user hasn't moved.
 *
 * @example
 * ```tsx
 * const { coords, address, isLoading, isReady } = useAutoLocation({
 *   enabled: opened,
 *   reverseGeocode: true,
 * });
 * ```
 */
export const useAutoLocation = (
  options: UseAutoLocationOptions
): UseAutoLocationReturn => {
  const {
    enabled,
    reverseGeocode = true,
    onlyOnce = true,
  } = options;

  const { requestLocation, error: contextError, permissionDenied } =
    useLocationHistoryContext();

  // Result of the most recent requestLocation call for this hook instance
  const [result, setResult] = useState<CachedLocation | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [hasTriggered, setHasTriggered] = useState(false);

  const prevEnabledRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ============================================
  // Reset when disabled (drawer closes)
  // ============================================

  useEffect(() => {
    if (!enabled && prevEnabledRef.current) {
      // Transitioned from enabled to disabled
      setResult(null);
      setLocalError(null);
      setIsRequesting(false);
      if (!onlyOnce) {
        setHasTriggered(false);
      }
    }
    prevEnabledRef.current = enabled;
  }, [enabled, onlyOnce]);

  // ============================================
  // Auto-trigger on enable
  // ============================================

  const performRequest = useCallback(async () => {
    if (!isMountedRef.current) return;

    setIsRequesting(true);
    setLocalError(null);

    try {
      const location = await requestLocation({ reverseGeocode });

      if (isMountedRef.current) {
        setResult(location);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setLocalError(
          err instanceof Error ? err.message : "Failed to get location"
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsRequesting(false);
      }
    }
  }, [requestLocation, reverseGeocode]);

  useEffect(() => {
    if (enabled && !hasTriggered) {
      setHasTriggered(true);
      performRequest();
    }
  }, [enabled, hasTriggered, performRequest]);

  // ============================================
  // Manual refresh
  // ============================================

  const refresh = useCallback(async () => {
    setLocalError(null);
    setIsRequesting(true);

    try {
      const location = await requestLocation({
        reverseGeocode,
        allowPrompt: true,
      });

      if (isMountedRef.current) {
        setResult(location);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setLocalError(
          err instanceof Error ? err.message : "Failed to get location"
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsRequesting(false);
      }
    }
  }, [requestLocation, reverseGeocode]);

  // ============================================
  // Clear
  // ============================================

  const clear = useCallback(() => {
    setResult(null);
    setLocalError(null);
    setHasTriggered(false);
    setIsRequesting(false);
  }, []);

  // ============================================
  // Derived Values
  // ============================================

  const coords: GeoCoords | null = result?.coords ?? null;
  const address: AddressData | null = result?.address ?? null;
  const isLoading = isRequesting;
  const isReady =
    !isLoading && coords !== null && (!reverseGeocode || address !== null);
  const error = localError || contextError;

  return {
    coords,
    address,
    cachedLocation: result,
    isLoading,
    isReady,
    error,
    permissionDenied,
    refresh,
    clear,
  };
};